/**
 * Open external Telegram deep links from inside the Mini App.
 *
 * Used for the bot-activation plaque and referral sharing (дельта §1): a ``t.me/<bot>``
 * link that STARTs the bot so it may DM the user. Prefers the native
 * ``openTelegramLink`` (keeps the user inside Telegram); falls back to ``window.open``
 * outside the Telegram environment (e.g. a browser preview or tests).
 */
import { openTelegramLink as sdkOpenTelegramLink } from "@telegram-apps/sdk-react";

/**
 * Open a ``https://t.me/...`` link inside Telegram when possible, else in a new tab.
 *
 * Never throws: any SDK/environment failure falls back to ``window.open``.
 */
export function openTelegramLink(url: string): void {
  try {
    if (sdkOpenTelegramLink.isAvailable()) {
      sdkOpenTelegramLink(url);
      return;
    }
  } catch {
    /* fall through to the browser fallback */
  }
  try {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch {
    /* nothing more we can do */
  }
}

/**
 * Build a ``https://t.me/<bot>?start=<payload>`` deep link.
 *
 * The classic ``?start=`` form makes the invited user actually START the bot (opens a
 * chat the bot can DM and runs the ``/start`` handler). ``botUsername`` must be the bare
 * username (no leading ``@``); returns ``null`` when it is missing so callers can hide
 * the action rather than build a broken link.
 */
export function botStartLink(
  botUsername: string | undefined,
  startParam: string,
): string | null {
  const username = botUsername?.replace(/^@/, "").trim();
  if (!username) {
    return null;
  }
  const suffix = startParam.length > 0 ? `?start=${encodeURIComponent(startParam)}` : "";
  return `https://t.me/${username}${suffix}`;
}
